import { useState } from "react";
import {
  useTenantSubscription,
  useTenantInvoices,
  useCreateCheckoutSession,
  useInvoiceLinks,
  useFiscalInvoicePdf,
  type TenantInvoice,
} from "../api/hooks";
import { DataTable, createColumnHelper } from "../components/DataTable";
import {
  canPayInvoice,
  missingInvoiceUrlMessage,
  pickInvoiceUrl,
  type InvoiceLinkKind,
} from "../lib/invoice-links";
import { fiscalPdfFilename, openBlankTab, showPdfBlob } from "../lib/pdf-open";
import {
  Button,
  Chip,
  ErrorNotice,
  Notice,
  PageHeader,
  Spinner,
} from "../components/ui";

/**
 * Módulo financiero del tenant (CLAUDE.md Fase 7, paso 10).
 *
 * SKILL.md §7 — REGLA CRÍTICA: esta página NO tiene `assertTenantCanOperate`
 * en el backend que la sirve. Un tenant BLOQUEADO o PENDING_PAYMENT debe
 * poder verla y pagar para desbloquear su cuenta.
 *
 * Muestra:
 * - Banner de alerta si la suscripción está BLOCKED o PENDING_PAYMENT.
 * - Resumen del plan actual (nombre, estado, periodicidad, próxima renovación).
 * - Botón para añadir/cambiar método de pago (Stripe Checkout, modo "setup").
 * - Historial de facturas paginado con botón "Pagar" por factura pendiente: abre la página de pago de la
 *   propia factura de Stripe, para que el cobro llegue como `invoice.paid` y se concilie solo.
 */

const STATUS_TONE: Record<string, "ok" | "warn" | "danger" | "neutral"> = {
  TRIAL: "neutral",
  ACTIVE: "ok",
  PENDING_PAYMENT: "warn",
  BLOCKED: "danger",
  CANCELLED: "neutral",
};

const STATUS_LABEL: Record<string, string> = {
  TRIAL: "Prueba",
  ACTIVE: "Activa",
  PENDING_PAYMENT: "Pendiente de pago",
  BLOCKED: "Bloqueada",
  CANCELLED: "Cancelada",
};

const INVOICE_STATUS_TONE: Record<string, "ok" | "warn" | "danger" | "neutral"> = {
  DRAFT: "neutral",
  ISSUED: "warn",
  PAID: "ok",
  OVERDUE: "danger",
  CANCELLED: "neutral",
};

const INVOICE_STATUS_LABEL: Record<string, string> = {
  DRAFT: "Borrador",
  ISSUED: "Emitida",
  PAID: "Pagada",
  OVERDUE: "Vencida",
  CANCELLED: "Cancelada",
};

const PERIODICITY_LABEL: Record<string, string> = {
  MONTHLY: "Mensual",
  QUARTERLY: "Trimestral",
  ANNUAL: "Anual",
};

function formatDate(value: string | null | undefined): string {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("es-ES");
}

function formatAmount(value: string, currency: string): string {
  return new Intl.NumberFormat("es-ES", { style: "currency", currency }).format(Number(value));
}

const column = createColumnHelper<TenantInvoice>();

export function FinancialPage() {
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const subscriptionQuery = useTenantSubscription();
  const invoicesQuery = useTenantInvoices({ page, pageSize });
  const createCheckout = useCreateCheckoutSession();
  const invoiceLinks = useInvoiceLinks();
  const fiscalPdf = useFiscalInvoicePdf();
  const [linkError, setLinkError] = useState<string | null>(null);

  const subscription = subscriptionQuery.data;
  const isBlocked =
    subscription?.status === "BLOCKED" || subscription?.status === "PENDING_PAYMENT";

  function handleSetupPaymentMethod() {
    createCheckout.mutate(
      { mode: "setup", paymentMethodTypes: ["card", "sepa_debit"] },
      {
        onSuccess: (result) => {
          window.location.href = result.url;
        },
      },
    );
  }

  /**
   * Abre la factura (página), su PDF o su página de pago en otra pestaña. La pestaña se abre ya, en el clic,
   * porque los navegadores bloquean las ventanas que se abren después de esperar a la red; luego se le da
   * la dirección. "Pagar" abre la factura de Stripe (no un cobro suelto): el pago llega como `invoice.paid`
   * y la factura y la suscripción se concilian solas.
   */
  function openInvoice(invoice: TenantInvoice, kind: InvoiceLinkKind) {
    setLinkError(null);
    const tab = window.open("", "_blank");
    invoiceLinks.mutate(invoice.id, {
      onSuccess: (links) => {
        const url = pickInvoiceUrl(links, kind);
        if (!url) {
          tab?.close();
          setLinkError(missingInvoiceUrlMessage(kind));
          return;
        }
        if (tab) tab.location.href = url;
        else window.location.href = url;
      },
      onError: (err) => {
        tab?.close();
        setLinkError(err instanceof Error ? err.message : "No se pudo obtener la factura.");
      },
    });
  }

  function openFiscalInvoice(invoice: TenantInvoice) {
    setLinkError(null);
    const tab = openBlankTab();
    fiscalPdf.mutate(invoice.id, {
      onSuccess: (blob) =>
        showPdfBlob(blob, tab, fiscalPdfFilename(invoice.fiscalReference, invoice.kind === "RECTIFICATIVE")),
      onError: (err) => {
        tab?.close();
        setLinkError(err instanceof Error ? err.message : "No se pudo obtener la factura fiscal.");
      },
    });
  }

  const invoiceColumns = [
    column.accessor("periodStart", {
      header: "Período",
      cell: (info) => {
        const invoice = info.row.original;
        if (invoice.kind === "RECTIFICATIVE") return "Anulación de factura";
        const start = formatDate(info.getValue());
        const end = formatDate(invoice.periodEnd);
        return `${start} – ${end}`;
      },
    }),
    column.display({
      id: "fiscalReference",
      header: "Nº factura",
      cell: (info) => {
        const invoice = info.row.original;
        return (
          <span className="whitespace-nowrap">
            {invoice.fiscalReference ?? "—"}
            {invoice.kind === "RECTIFICATIVE" ? (
              <>
                {" "}
                <Chip tone="warn">Rectificativa</Chip>
              </>
            ) : null}
          </span>
        );
      },
    }),
    column.accessor("netAmount", {
      header: "Base",
      cell: (info) => formatAmount(info.getValue(), info.row.original.currency),
    }),
    column.accessor("taxAmount", {
      header: "Impuestos",
      cell: (info) =>
        Number(info.getValue()) > 0 ? formatAmount(info.getValue(), info.row.original.currency) : "—",
    }),
    column.accessor("totalAmount", {
      header: "Total",
      cell: (info) => formatAmount(info.getValue(), info.row.original.currency),
    }),
    column.accessor("status", {
      header: "Estado",
      cell: (info) => {
        const s = info.getValue();
        return (
          <Chip tone={INVOICE_STATUS_TONE[s] ?? "neutral"}>
            {INVOICE_STATUS_LABEL[s] ?? s}
          </Chip>
        );
      },
    }),
    column.accessor("dueDate", {
      header: "Vencimiento",
      cell: (info) => formatDate(info.getValue()),
    }),
    column.accessor("paidAt", {
      header: "Pagada el",
      cell: (info) => formatDate(info.getValue()),
    }),
    column.display({
      id: "actions",
      header: "Acciones",
      cell: (info) => {
        const invoice = info.row.original;
        const canPay = canPayInvoice(invoice);
        return (
          <div className="flex gap-2">
            {invoice.hasProviderInvoice ? (
              <>
                <Button variant="quiet" onClick={() => openInvoice(invoice, "view")} disabled={invoiceLinks.isPending}>
                  Ver
                </Button>
                <Button variant="quiet" onClick={() => openInvoice(invoice, "pdf")} disabled={invoiceLinks.isPending}>
                  PDF
                </Button>
              </>
            ) : null}
            {invoice.hasFiscalInvoice ? (
              <Button variant="quiet" onClick={() => openFiscalInvoice(invoice)} disabled={fiscalPdf.isPending}>
                Factura fiscal
              </Button>
            ) : null}
            {canPay && (
              <Button variant="primary" onClick={() => openInvoice(invoice, "pay")} disabled={invoiceLinks.isPending}>
                {invoiceLinks.isPending ? "…" : "Pagar"}
              </Button>
            )}
          </div>
        );
      },
    }),
  ];

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Tu cuenta" title="Financiero" description="Gestiona tu suscripción, método de pago y facturas." />

      {/* ─── Banner BLOCKED / PENDING_PAYMENT ──────────────────────────── */}
      {isBlocked && subscription && (
        <Notice tone="danger">
          <strong>
            {subscription.status === "BLOCKED"
              ? "Tu cuenta está bloqueada por impago."
              : "Hay un pago pendiente en tu suscripción."}
          </strong>{" "}
          Para reactivar el servicio, añade un método de pago o paga la factura
          pendiente desde la tabla inferior.
        </Notice>
      )}

      {/* ─── Suscripción ──────────────────────────────────────────────── */}
      <section>
        <h2 className="text-lg font-semibold mb-3">Suscripción</h2>

        {subscriptionQuery.isPending && <Spinner />}
        {subscriptionQuery.isError && (
          <ErrorNotice error={subscriptionQuery.error} onRetry={() => subscriptionQuery.refetch()} />
        )}

        {subscription && (
          <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
            <div className="flex flex-wrap gap-6">
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Plan</p>
                <p className="font-semibold">{subscription.billingPlanName}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Estado</p>
                <Chip tone={STATUS_TONE[subscription.status] ?? "neutral"}>
                  {STATUS_LABEL[subscription.status] ?? subscription.status}
                </Chip>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Periodicidad</p>
                <p>{PERIODICITY_LABEL[subscription.periodicity] ?? subscription.periodicity}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Próxima renovación</p>
                <p>{formatDate(subscription.currentPeriodEnd)}</p>
              </div>
              {subscription.trialEndsAt && (
                <div>
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Fin del período de prueba</p>
                  <p>{formatDate(subscription.trialEndsAt)}</p>
                </div>
              )}
              <div>
                <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Método de pago</p>
                <p>{subscription.hasPaymentMethodOnFile ? "Guardado ✓" : "Sin método de pago"}</p>
              </div>
            </div>

            <div className="pt-2 border-t border-gray-100 flex gap-3">
              <Button
                variant="primary"
                onClick={handleSetupPaymentMethod}
                disabled={createCheckout.isPending}
              >
                {subscription.hasPaymentMethodOnFile
                  ? "Cambiar método de pago"
                  : "Añadir método de pago"}
              </Button>
            </div>
          </div>
        )}
      </section>

      {/* ─── Historial de facturas ───────────────────────────────────── */}
      <section>
        <h2 className="text-lg font-semibold mb-3">Facturas</h2>

        {linkError && <Notice tone="danger">{linkError}</Notice>}
        {invoicesQuery.isPending && <Spinner />}
        {invoicesQuery.isError && (
          <ErrorNotice
            error={invoicesQuery.error}
            onRetry={() => invoicesQuery.refetch()}
          />
        )}

        {invoicesQuery.data && (
          <>
            <DataTable
              columns={invoiceColumns}
              data={invoicesQuery.data.items}
              emptyMessage="No hay facturas todavía."
            />
            {/* Paginación simple */}
            {invoicesQuery.data.total > pageSize && (
              <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
                <span>
                  Mostrando {(page - 1) * pageSize + 1}–
                  {Math.min(page * pageSize, invoicesQuery.data.total)} de{" "}
                  {invoicesQuery.data.total}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="quiet"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="quiet"
                    disabled={page * pageSize >= invoicesQuery.data.total}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {/* Error del checkout (si la mutación falla) */}
      {createCheckout.isError && (
        <Notice tone="danger">
          No se pudo iniciar el pago. Inténtalo de nuevo o contacta con soporte.
        </Notice>
      )}
    </div>
  );
}
